/**
 * Legend of the Consolidated view: the bucket roles and edge kinds that are drawn, with the same
 * colours as the picture (roleStyle / EDGE_KIND_STYLE). Open by default, as the report figure's
 * legend is always shown; the reader can fold it away.
 */
import { useState } from 'react';
import type { ConsolidatedBucket, ConsolidatedEdgeKind, ConsolidatedEdge } from './types';
import { ROLE_LABEL, roleStyle, EDGE_KIND_STYLE } from './roleStyle';
import { WALLET_ROLE_LABEL } from '../../constants/walletRole';

const WALLET_ROLE_KEY = ['exchange_deposit', 'exchange_hot', 'card_offramp', 'customer_wallet'] as const;

export const EDGE_KIND_LABEL: Record<ConsolidatedEdgeKind, string> = {
  deposit: 'Deposit',
  sweep: 'Sweep',
  withdrawal: 'Withdrawal',
  forward: 'Forward transfer',
  identity_link: 'Identity link',
  other: 'Other link',
};

/** Roles and edge kinds actually present, in first-seen order. Pure (tested without a DOM). */
export function legendEntries(buckets: ConsolidatedBucket[], edges: ConsolidatedEdge[]) {
  const roles = [...new Set(buckets.filter((b) => b.role !== 'context').map((b) => b.role))];
  const kinds = [...new Set(edges.map((e) => e.kind))];
  return { roles, kinds };
}

export function ConsolidatedLegend({ buckets, edges }: { buckets: ConsolidatedBucket[]; edges: ConsolidatedEdge[] }) {
  const [open, setOpen] = useState(true);
  const { roles, kinds } = legendEntries(buckets, edges);
  return (
    <div
      className="absolute bottom-3 right-3 z-10 w-52 rounded-md border border-border bg-card text-[11px] shadow"
      data-testid="consol-legend"
      data-open={open ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="consol-legend-toggle"
        className="flex w-full items-center justify-between px-2.5 py-1.5 text-left font-semibold"
      >
        Legend <span className="text-muted-foreground" aria-hidden>{open ? '–' : '+'}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-border px-2.5 py-2">
          <ul className="space-y-1" data-testid="consol-legend-roles">
            {roles.map((r) => {
              const st = roleStyle(r);
              return (
                <li key={r} className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-5 shrink-0 rounded-sm"
                    style={{ background: st.fill, border: `1.5px ${st.dashed ? 'dashed' : 'solid'} ${st.stroke}` }}
                    aria-hidden
                  />
                  <span>{ROLE_LABEL[r] ?? r}</span>
                </li>
              );
            })}
          </ul>
          <ul className="space-y-1 border-t border-border pt-2" data-testid="consol-wallet-roles">
            {WALLET_ROLE_KEY.map((role) => (
              <li key={role}>{WALLET_ROLE_LABEL[role]}</li>
            ))}
          </ul>
          {kinds.length > 0 && (
            <ul className="space-y-1 border-t border-border pt-2" data-testid="consol-legend-edges">
              {kinds.map((k) => {
                const st = EDGE_KIND_STYLE[k] ?? EDGE_KIND_STYLE.other;
                return (
                  <li key={k} className="flex items-center gap-2">
                    <span
                      className="inline-block w-5 shrink-0"
                      style={{ borderTop: `2px ${st.dashed ? 'dashed' : 'solid'} ${st.color}` }}
                      aria-hidden
                    />
                    <span>{EDGE_KIND_LABEL[k] ?? k}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
